import { api } from "./api";

/**
 * HB-NOTIFY-01 클라이언트 — HairBridge 알림 구독.
 * 계약: docs/product/notification-channel.md §5.6 · §5.7
 */

export interface NotificationPreferences {
  pushEnabled: boolean;
  bookingCreated: boolean;
  bookingCancelled: boolean;
  waitlistSlotOpen: boolean;
  rebookingDue: boolean;
  dayDigest: boolean;
  quietStartHour: number;
  quietEndHour: number;
  digestHour: number;
}

export interface PushSubscriptionRow {
  id: string;
  user_agent: string | null;
  created_at: string;
  last_success_at: string | null;
  failure_count: number;
}

export interface NotificationSettings {
  configured: boolean;
  vapidPublicKey: string | null;
  preferences: NotificationPreferences;
  subscriptions: PushSubscriptionRow[];
}

/**
 * 이 기기에서 웹푸시가 가능한 상태인지 판정한다.
 *
 * iOS·iPadOS는 홈 화면에 추가된 웹앱에서만 푸시가 동작한다. Safari 탭에서는
 * 권한 요청 자체가 의미 없으므로 버튼 대신 안내를 보여 준다.
 */
export type PushCapability =
  | { state: "ready" }
  | { state: "unsupported"; reason: "no_service_worker" | "no_push_manager" }
  | { state: "needs_home_screen" }
  | { state: "denied" };

export function isStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

export function isIos(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+는 Mac으로 보고하므로 터치 포인트로 구분한다.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
}

export function pushCapability(): PushCapability {
  if (!("serviceWorker" in navigator)) {
    return { state: "unsupported", reason: "no_service_worker" };
  }
  if (!("PushManager" in window)) {
    if (isIos() && !isStandalone()) return { state: "needs_home_screen" };
    return { state: "unsupported", reason: "no_push_manager" };
  }
  if (isIos() && !isStandalone()) return { state: "needs_home_screen" };
  if (Notification.permission === "denied") return { state: "denied" };
  return { state: "ready" };
}

/**
 * VAPID 공개키(base64url)를 applicationServerKey가 받는 ArrayBuffer로 바꾼다.
 * Uint8Array<ArrayBufferLike>는 BufferSource에 대입되지 않으므로 buffer를 직접 넘긴다.
 */
function urlBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

export async function fetchNotificationSettings(
  token: string,
): Promise<NotificationSettings> {
  return api<NotificationSettings>("/api/v1/business/notifications/settings", {}, token);
}

export async function saveNotificationPreferences(
  token: string,
  preferences: NotificationPreferences,
): Promise<void> {
  await api(
    "/api/v1/business/notifications/settings",
    { method: "PUT", body: JSON.stringify(preferences) },
    token,
  );
}

/**
 * 브라우저 권한을 요청하고 구독을 서버에 저장한다.
 * 이미 구독이 있으면 그대로 재사용해 endpoint 중복을 만들지 않는다.
 */
export async function subscribeToPush(
  token: string,
  vapidPublicKey: string,
): Promise<{ ok: true; id: string } | { ok: false; reason: string }> {
  const capability = pushCapability();
  if (capability.state !== "ready") return { ok: false, reason: capability.state };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "permission_denied" };

  const registration = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
  if (!registration) return { ok: false, reason: "no_service_worker" };
  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(vapidPublicKey),
    }));

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return { ok: false, reason: "invalid_subscription" };
  }

  const result = await api<{ id: string }>(
    "/api/v1/business/notifications/subscriptions",
    {
      method: "POST",
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        userAgent: window.navigator.userAgent.slice(0, 256),
      }),
    },
    token,
  );
  return { ok: true, id: result.id };
}

export async function unsubscribeFromPush(
  token: string,
  subscriptionId: string,
): Promise<void> {
  await api(
    `/api/v1/business/notifications/subscriptions/${subscriptionId}`,
    { method: "DELETE" },
    token,
  );
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  await subscription?.unsubscribe();
}

export async function sendTestPush(token: string): Promise<number> {
  const result = await api<{ queued: number }>(
    "/api/v1/business/notifications/test",
    { method: "POST" },
    token,
  );
  return result.queued;
}
